import { useEffect, useState } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useDBA } from '@/contexts/DBAContext';
import api from '@/lib/api';
import { LogOut, Menu, X } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { LayoutNavigation, MobileBottomNavigation } from '@/components/layout/LayoutNavigation';

export default function Layout() {
  const { user, logout } = useAuth();
  const { dbas, selectedDBA, setSelectedDBA } = useDBA();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [healthAlertCount, setHealthAlertCount] = useState(0);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin';
  const permissions = user?.permissions || {};

  useEffect(() => {
    let mounted = true;
    if (!isAdmin) {
      setHealthAlertCount(0);
      return () => { mounted = false; };
    }
    api.get('/external/leads/health-alerts')
      .then(({ data }) => {
        if (mounted) setHealthAlertCount(Number(data?.summary?.total || 0));
      })
      .catch(() => {
        if (mounted) setHealthAlertCount(0);
      });
    return () => { mounted = false; };
  }, [isAdmin]);

  return (
    <div className="flex min-h-screen bg-[#FAFAFA] text-zinc-950">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      <aside
        className={`fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 h-screen w-[min(82vw,18rem)] lg:w-64 bg-white text-zinc-950 flex flex-col border-r border-zinc-200 transition-transform duration-200 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="sidebar"
      >
        <div className="border-b border-zinc-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 items-center gap-2" data-testid="app-sidebar-brand">
              <img src="/bcjr-logo.png" alt="Butler County Junk Removal" className="h-10 w-10 shrink-0 rounded-sm border border-zinc-200 bg-white object-contain p-0.5" data-testid="app-sidebar-logo" />
              <span className="truncate text-sm font-black uppercase leading-tight tracking-tight text-zinc-950 font-heading">Butler County<br className="hidden sm:block" /> Junk Removal</span>
            </div>
            <button className="lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-close-button">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="border-b border-zinc-200 bg-zinc-50 p-3">
          <label className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">
            Business Unit
          </label>
          <Select
            value={selectedDBA || 'all'}
            onValueChange={(v) => setSelectedDBA(v === 'all' ? null : v)}
          >
            <SelectTrigger
              className="h-11 rounded-sm border-zinc-300 bg-white text-sm text-zinc-950 focus:ring-[#002FA7]"
              data-testid="dba-switcher"
            >
              <SelectValue placeholder="All Businesses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Businesses</SelectItem>
              {dbas.map((dba) => (
                <SelectItem key={dba.id} value={dba.id}>
                  <span className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full inline-block"
                      style={{ backgroundColor: dba.color }}
                    />
                    {dba.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <LayoutNavigation isAdmin={isAdmin} permissions={permissions} healthAlertCount={healthAlertCount} onNavigate={() => setSidebarOpen(false)} />

        <div className="border-t border-zinc-200 bg-zinc-50 p-3">
          <div className="flex items-center justify-between border border-zinc-200 bg-white p-2">
            <div className="truncate">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-zinc-500 capitalize">{user?.role?.replace('_', ' ')}</p>
              {user?.username && <p className="text-[10px] text-zinc-500" data-testid="sidebar-username">@{user.username}</p>}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-zinc-500 hover:bg-zinc-100 hover:text-zinc-950"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-x-hidden">
        <div className="sticky top-0 z-30 flex items-center justify-between border-b border-zinc-950 bg-white/95 px-4 py-3 backdrop-blur lg:hidden" data-testid="mobile-top-bar">
          <button className="flex h-11 w-11 items-center justify-center rounded-sm border border-zinc-200 bg-white text-zinc-900" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-toggle" aria-label="Open navigation">
            <Menu className="w-5 h-5" />
          </button>
          <span className="flex items-center gap-2 font-bold tracking-tight font-heading" data-testid="app-mobile-brand"><img src="/bcjr-logo.png" alt="Butler County Junk Removal" className="h-8 w-8 rounded-full bg-white object-contain" data-testid="app-mobile-logo" />BCJR</span>
          <button className="flex h-11 w-11 items-center justify-center rounded-sm border border-zinc-200 bg-white text-zinc-900" onClick={handleLogout} data-testid="mobile-logout-button" aria-label="Log out"><LogOut className="w-5 h-5" /></button>
        </div>
        <div className="mx-auto w-full max-w-[1800px] px-4 pb-28 pt-4 sm:px-6 lg:px-8 lg:pb-8 lg:pt-8" data-testid="app-content-shell">
          <Outlet />
        </div>
      </main>
      <MobileBottomNavigation isAdmin={isAdmin} permissions={permissions} healthAlertCount={healthAlertCount} onMore={() => setSidebarOpen(true)} />
    </div>
  );
}
